import { useRef, useState } from "react";
import { WELDMAP_ALANLARI, hedefEsit, weldMapEtiketi, type GelenHedef, type WeldMapKarti, type WeldMapKartKaynagi } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  WELDMAP_UCU,
  okumaDugmesi,
  okumaEylemleri,
  weldMapEngelMetni,
  weldMapKaynakHaritada,
  weldMapKaynakOasis,
  weldMapKaynakSonucu,
  weldMapNedenMetni,
  weldMapNotMetinleri,
  weldMapPasoKesinligi,
  weldMapPasoSatiri,
  weldMapAcikTarihMetni,
  weldMapTarihMetni,
  weldMapYerOneriMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 2 · PR 10: Documents kartının weld map "Document reading" bölümü (prototip belgeler--weldmap-okuma). Okuma yalnız kuralların kullandığı
 * değerleri gösterir (sözleşme §5): başlıkta 8 değer, kaynak başına çizim ucu, satır başına 8 değer. OASIS işi önerir; "Use this place" kartın
 * bugünkü tür+yer kaydını çağırır (yeri kişi kaydeder; okuma yazmaz), "Use these pages" yalnız formu doldurur. Weld map'ten okunan satırlar paso
 * kaydı değildir (K24). Kaynak tarihi dosyalamadan sonra, kaynak başına "Record weld date" ile kaydedilir (kalite lideri, PM; yalnız boş tarih
 * dolar). Uyarıların kararı PR 8'in kutusudur (`UyariSatiri`). `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Record weld date"
 * yalnız yazılabilir kaynakta kalır (Z40).
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

/** Sayfa aralığının metni ("3" ya da "1-2"). */
const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");
/** Kaynağın ilk satırının sayfası (kaynak tarihinin "Open the page first" bağlantısı). */
const ilkSayfa = (k: WeldMapKartKaynagi): number | null => k.pasolar.flatMap((p) => p.degerler).find((d) => d.alan === "tarih" && d.sayfa !== null)?.sayfa ?? null;

export interface WeldMapFormYardimi {
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
  /** "Use these pages": Pages alanı (Partial) */
  sayfalar: (s: string) => void;
}

export function WeldMapOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: WeldMapKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: WeldMapFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  /** onay kutusu açık kaynak (Z26) */
  const [onay, setOnay] = useState<number | null>(null);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const y = k.yer;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const kalem = k.degerler.find((d) => d.alan === "kalem")?.deger ?? null;
  const satirSayisi = k.kaynaklar.reduce((t, x) => t + x.pasolar.length, 0);
  /** KIM-02 açıklaması: yerin riseri (yer metninin ilk parçası) ve uyarının hattı. */
  const uyariYeri = (hat: string | null) => {
    const rn = y?.yerMetni?.split(" · ")[0];
    return rn && hat ? { rn, hat } : null;
  };
  const notlar = k.karsilastirma ? weldMapNotMetinleri(k.karsilastirma.notlar) : [];
  const toplamUyari = k.karsilastirma?.uyarilar.length ?? 0;

  return (
    <div data-bolum="weldmap-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the weld map for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Weld map p.{0}", aralik(o.sayfalar)) : null,
              o.grafikSayfalari ? cevir("PWHT chart p.{0} (read later with the PWHT chart)", aralik(o.grafikSayfalari)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} welds, {1} rows", k.kaynaklar.length, satirSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
            <li className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]">
              <span>
                <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                {o.sayfalar ? <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: o.sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(o.sayfalar)}</a> : "—"}
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="block text-xs text-ink-3">QAF 038</span>
                <span className="block font-medium">{kalem ? cevir("Weld map · {0}", kalem) : cevir("Weld map")}</span>
              </span>
              <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                {y?.yerMetni && <span className="mr-1.5">{y.yerMetni}</span>}
                {y && y.durum !== "yok"
                  ? <Etiket tone={durumTonu(y.durum)}>{y.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
                  : <span className="text-ink-2">{y?.neden ? weldMapNedenMetni(y.neden) : "—"}</span>}
                {y?.durum === "oneri" && <span className="mt-0.5 block text-xs text-ink-3">{weldMapYerOneriMetni(k.degerler)}</span>}
                {yardim && y?.durum === "oneri" && y.yer && !hedefEsit(y.yer, hedef) && (
                  <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === "yer"} onClick={() => calistir("yer", () => yardim.yerKaydet(y.yer!))}>{cevir("Use this place")}</Buton>
                )}
              </span>
            </li>
            {o.grafikSayfalari && (
              <li className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]">
                <span>
                  <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                  <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: o.grafikSayfalari.bas })} target="_blank" rel="noreferrer">p.{aralik(o.grafikSayfalari)}</a>
                </span>
                <span className="min-w-0">
                  <span className="block font-medium">{cevir("PWHT chart")}</span>
                  <span className="block text-xs text-ink-3">{cevir("Not read with the weld map")}</span>
                </span>
                <span className="col-span-2 @min-[40rem]:col-span-1">—</span>
              </li>
            )}
          </ul>
          {yardim && o.sayfalar && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.sayfalar(aralik(o.sayfalar!))}>{cevir("Use these pages")}</Buton>
              <span className="text-xs text-ink-3">{cevir("Nothing is filed until you confirm.")}</span>
            </div>
          )}

          <AltBaslik>{cevir("Header")}</AltBaslik>
          <ul className="space-y-0.5 text-sm">
            {WELDMAP_ALANLARI.map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[11rem] shrink-0 text-ink-2">{cevir(weldMapEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {d?.deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          {k.kaynaklar.length > 0 && (
            <>
              <AltBaslik>{cevir("Welds on the weld map and in OASIS")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Weld")}</th>
                    <th className={TABLO_BASI}>{cevir("On the weld map")}</th>
                    <th className={TABLO_BASI}>{cevir("In OASIS (this job)")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.kaynaklar.map((x) => (
                      <tr key={x.no} data-kaynak={x.no}>
                        <td className={cn(TABLO_HUCRESI, "font-mono")}>W{x.no}</td>
                        <td className={TABLO_HUCRESI}>{weldMapKaynakHaritada(x) || "—"}</td>
                        <td className={TABLO_HUCRESI}>{weldMapKaynakOasis(x)}</td>
                        <td className={TABLO_HUCRESI}>{weldMapKaynakSonucu(x)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <AltBaslik>{cevir("Rows read from the weld map")}</AltBaslik>
              <p className="text-xs text-ink-3">{cevir("Read from the weld map. These are not pass records: the welding step counts the passes entered in OASIS, or the weld map PDF, as today.")}</p>
              <ul className="mt-1 space-y-0.5 text-sm">
                {k.kaynaklar.flatMap((x) => x.pasolar.map((p) => {
                  const ditto = p.degerler.filter((d) => d.yazildigi && d.yazildigi !== d.deger).map((d) => `${cevir(weldMapEtiketi(d.alan))} ${d.yazildigi}`);
                  return (
                    <li key={`${x.no}-${p.sira}`} className="flex gap-2">
                      <Nokta k={weldMapPasoKesinligi(p)} />
                      <span className="w-[7rem] shrink-0 text-ink-2">{cevir("W{0} · row {1}", x.no, p.sira)}</span>
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {weldMapPasoSatiri(p) || <span className="text-ink-3">{cevir("not read")}</span>}
                        {sayfaLinki(p.degerler.find((d) => d.sayfa !== null)?.sayfa ?? null)}
                        {ditto.length > 0 && <span className="block text-xs text-ink-3">{cevir("as written: {0}", ditto.join(", "))}</span>}
                      </span>
                    </li>
                  );
                }))}
              </ul>
              <p className="mt-1 text-xs text-ink-3">{cevir("Run, process, diameter, amps, volts and gas are not read: open the page to see them.")}</p>

              <AltBaslik>{cevir("Weld dates")}</AltBaslik>
              {!salt ? (
                <ul className="space-y-0.5 text-sm">
                  {k.kaynaklar.map((x) => (
                    <li key={x.no} className="flex gap-2">
                      <span className="w-[3rem] shrink-0 font-mono text-ink-2">W{x.no}</span>
                      <span className="min-w-0">
                        {weldMapAcikTarihMetni(x)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="overflow-x-auto rounded-md border border-line">
                  <table className="w-full text-sm" data-bolum="kaynak-tarihleri">
                    <thead><tr className="border-b border-line">
                      <th className={TABLO_BASI}>{cevir("Weld")}</th>
                      <th className={TABLO_BASI}>{cevir("On the weld map")}</th>
                      <th className={TABLO_BASI}>{cevir("Weld date in OASIS")}</th>
                      <th className={TABLO_BASI}>{cevir("Result")}</th>
                    </tr></thead>
                    <tbody className="divide-y divide-line">
                      {k.kaynaklar.map((x) => {
                        const kayitli = x.oasis?.kayitliTarih ?? null;
                        return (
                          <tr key={x.no} data-kaynak-tarihi={x.no}>
                            <td className={cn(TABLO_HUCRESI, "font-mono")}>W{x.no}</td>
                            <td className={TABLO_HUCRESI}>{x.tarih.gun && x.tarih.neden === null ? cevir("{0} on every row", tarih(x.tarih.gun)) : weldMapTarihMetni(x)}</td>
                            <td className={TABLO_HUCRESI}>{kayitli ? tarih(kayitli) : "—"}</td>
                            <td className={TABLO_HUCRESI}>
                              {x.kayit
                                ? <><span className="text-ok">{cevir("✓ recorded")}</span><span className="block text-xs text-ink-3">{cevir("recorded by {0} {1}", x.kayit.kim, tarihSaat(x.kayit.ts))}</span></>
                                : x.yazilabilir
                                  ? <Buton kucuk tur="birincil" disabled={mesgul !== null} onClick={() => setOnay(x.no)}>{cevir("Record weld date")}</Buton>
                                  : kayitli && x.tarih.gun && kayitli !== x.tarih.gun
                                    ? cevir("OASIS keeps its date")
                                    : x.engel
                                      ? <span className="text-ink-2">{weldMapEngelMetni(x.engel)}</span>
                                      : x.tarih.gun ? "—" : cevir("not proposed")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {onay !== null && (() => {
                const x = k.kaynaklar.find((z) => z.no === onay);
                if (!x?.tarih.gun) return null;
                const gun = x.tarih.gun;
                return (
                  <div className="mt-2 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm" data-bolum="kaynak-tarihi-onayi">
                    <p className="font-medium">{cevir("Record weld date {0}: {1}?", `W${x.no}`, tarih(gun))}</p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-2">
                      <li>{cevir("Open the page first: the date is read from every row of {0}.", `W${x.no}`)}{sayfaLinki(ilkSayfa(x))}</li>
                      <li>{cevir("The date is written to weld {0} of this job in OASIS. A weld that already has a date is never changed.", `W${x.no}`)}</li>
                      <li>{cevir("The record keeps who, when and the page; it is in the job's history.")}</li>
                      <li>{cevir("Nothing else changes: passes, fit-up forms, steps and the weld map screen stay as they are.")}</li>
                    </ul>
                    <div className="mt-2 flex gap-2">
                      <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "tarih"} onClick={() => calistir("tarih", async () => {
                        await api.post(WELDMAP_UCU.kaynakTarihi(satirId), { weldNo: x.no, gun });
                        setOnay(null);
                      })}>{cevir("Record")}</Buton>
                      <Buton kucuk disabled={mesgul !== null} onClick={() => setOnay(null)}>{cevir("Cancel")}</Buton>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {notlar.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {notlar.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={uyariYeri(u.hat)} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(WELDMAP_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(WELDMAP_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(WELDMAP_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
